import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Download, MapPinned, RefreshCw, ShieldAlert, ShieldCheck, Upload, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useRoleModules } from "../lib/roleModules";
import { api, errMsg } from "../lib/api";
import { staffLabelText } from "./StaffAvatar";
import { MohallaBackfillDialog } from "./MohallaBackfillDialog";
import { MohallaUploadDialog, downloadStrandedList } from "./MohallaUploadDialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

const n = (v) => Number(v || 0).toLocaleString("en-IN");
const plural = (v, one, many) => (Number(v) === 1 ? one : many);

/**
 * Phase 156 — Coverage alerts: wards whose properties have NO collector who can see them today.
 *   • "alert" rows   — the ward only has mohalla-level collectors, yet some properties carry no mohalla → stranded.
 *                      Fix: fill their mohallas (Mohalla Backfill) or map a whole-ward collector.
 *   • "none" rows    — no collector is mapped to the ward at all. Fix: map a collector under Users & Staff.
 * Reads GET /assignments/coverage-alerts (admin + supervisor). Backfill is a Municipal Admin action (masters.mohallas).
 *
 *   compact      tighter card for workspaces (fewer rows, smaller header)
 *   refreshKey   bump to reload (e.g. after a territory save / auto-assign run)
 *   hideWhenClear   render nothing when every ward is covered (dashboard keeps the positive state visible)
 */
export function useCoverageAlerts(code, refreshKey = 0) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const load = useCallback(async ({ silent = false } = {}) => {
    const my = ++seq.current;
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/t/${code}/assignments/coverage-alerts`);
      if (my !== seq.current) return;
      setData(r.data);
      setError(null);
    } catch (e) {
      if (my === seq.current) setError(e);
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [code]);
  useEffect(() => { load({ silent: refreshKey > 0 }); }, [load, refreshKey]);
  return { data, loading, error, reload: load };
}

export function CoverageAlertsCard({ code, compact = false, refreshKey = 0, hideWhenClear = false, className = "", onChanged }) {
  const { auth } = useAuth();
  const access = useRoleModules(code);
  const isAdmin = auth?.user?.role === "municipal_admin";
  const canBackfill = isAdmin && access.canWrite("masters", "mohallas");
  const { data, loading, error, reload } = useCoverageAlerts(code, refreshKey);
  const [open, setOpen] = useState(!compact);
  const [backfillWard, setBackfillWard] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);      // Phase 186
  const [downloading, setDownloading] = useState(false);

  const rows = data?.rows || [];
  const s = data?.summary || {};
  const total = rows.length;
  const limit = compact ? 3 : 5;
  const visible = showAll ? rows : rows.slice(0, limit);
  const clear = data && total === 0;
  const noMohalla = data ? Number(s.no_mohalla_total || 0) : null;   // every active property with a ward but no mohalla

  if (clear && hideWhenClear) return null;
  if (error?.response?.status === 403) return null;   // not allowed to see coverage data — stay out of the way

  const afterBackfill = () => { reload({ silent: true }); onChanged?.(); };
  const territoriesHref = (r) => `/${code}/admin/users${r?.zone_id ? `?zone_id=${r.zone_id}` : ""}`;
  const downloadList = async () => {
    setDownloading(true);
    try {
      await downloadStrandedList(code);
      toast.success(`List of ${n(noMohalla)} ${plural(noMohalla, "property", "properties")} without a mohalla downloaded`, {
        description: "Fill the \"Correct Mohalla\" column and use Bulk Upload to apply it.",
      });
    } catch (e) { toast.error(errMsg(e)); } finally { setDownloading(false); }
  };

  return (
    <Card className={`shadow-sm ${clear ? "" : "border-amber-300/70"} ${className}`} data-testid="coverage-alerts-card" data-state={loading && !data ? "loading" : error ? "error" : clear ? "clear" : "alerts"}>
      <CardContent className={compact ? "p-3 space-y-2" : "p-4 space-y-3"}>
        {/* header */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${clear ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} aria-hidden="true">
              {clear ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium flex items-center gap-2" data-testid="coverage-alerts-title">
                Coverage alerts
                {!loading && data && !clear && (
                  <Badge variant="outline" className="text-[11px] bg-amber-100 text-amber-800 border-amber-200 tabular-nums" data-testid="coverage-alerts-count">
                    {n(total)} {plural(total, "ward", "wards")}
                  </Badge>
                )}
              </div>
              {loading && !data ? (
                <Skeleton className="h-3 w-72 mt-1.5" />
              ) : error ? (
                <p className="text-xs text-destructive mt-0.5" data-testid="coverage-alerts-error">{errMsg(error)}</p>
              ) : clear ? (
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="coverage-alerts-clear">Every ward with properties has a collector who can see them. Nothing is stranded.</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="coverage-alerts-summary">
                  {s.alert_wards > 0 && (
                    <span data-testid="coverage-alerts-stranded"><span className="font-semibold text-amber-800">{n(s.stranded_no_mohalla)}</span> {plural(s.stranded_no_mohalla, "property has", "properties have")} no mohalla in {n(s.alert_wards)} {plural(s.alert_wards, "ward", "wards")} covered only by mohalla — no collector can see them.</span>
                  )}
                  {s.alert_wards > 0 && s.uncovered_wards > 0 && " "}
                  {s.uncovered_wards > 0 && (
                    <span data-testid="coverage-alerts-uncovered"><span className="font-semibold">{n(s.uncovered_wards)}</span> {plural(s.uncovered_wards, "ward has", "wards have")} no collector at all ({n(s.uncovered_properties)} {plural(s.uncovered_properties, "property", "properties")}).</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {canBackfill && !clear && (
              <Button size="sm" variant={compact ? "outline" : "default"} className="h-8" onClick={() => setBackfillWard("")} data-testid="coverage-alerts-backfill-button">
                <MapPinned className="h-3.5 w-3.5 mr-1.5" /> Mohalla Backfill
              </Button>
            )}
            {canBackfill && (
              <TooltipProvider delayDuration={200}>
                {/* Phase 186: offline correction loop — download every property without a mohalla, fill "Correct Mohalla", upload */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline" className="h-8" onClick={downloadList} disabled={downloading || noMohalla === 0}
                      data-testid="coverage-alerts-download-list-button" data-count={noMohalla ?? ""}>
                      <Download className={`h-3.5 w-3.5 mr-1.5 ${downloading ? "animate-pulse" : ""}`} />
                      {downloading ? "Preparing…" : "Download List"}
                      {noMohalla > 0 && <span className="ml-1.5 rounded bg-muted px-1 text-[10px] tabular-nums text-muted-foreground" data-testid="coverage-alerts-download-list-count">{n(noMohalla)}</span>}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    {noMohalla === 0
                      ? "Every active property with a ward already has a mohalla."
                      : `Excel of ${n(noMohalla)} ${plural(noMohalla, "property", "properties")} without a mohalla (all wards): PIN, owner, address, ward, zone, current mohalla and a blank "Correct Mohalla" column to fill in and upload.`}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setUploadOpen(true)} data-testid="coverage-alerts-bulk-upload-button">
                      <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk Upload
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">Upload the completed list — mohallas are set for every PIN in the file (preview first, undo-able).</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isAdmin && !compact && (
              <Button asChild size="sm" variant="outline" className="h-8" data-testid="coverage-alerts-unassigned-link">
                <Link to={`/${code}/admin/unassigned-wards`}>Unassigned wards</Link>
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => reload()} disabled={loading} aria-label="Refresh coverage alerts" data-testid="coverage-alerts-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {!clear && (
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="coverage-alerts-toggle">
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                <span className="sr-only">{open ? "Collapse" : "Expand"}</span>
              </Button>
            )}
          </div>
        </div>

        {/* rows */}
        {open && !clear && (loading && !data ? (
          <div className="space-y-2" aria-hidden="true">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : error ? (
          <Button size="sm" variant="outline" onClick={() => reload()} data-testid="coverage-alerts-retry">Retry</Button>
        ) : (
          <ul className="divide-y rounded-lg border overflow-hidden" data-testid="coverage-alerts-list">
            {visible.map((r) => (
              <li key={r.ward_id} className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-3 ${compact ? "py-2" : "py-2.5"} ${r.alert ? "bg-amber-50/60 dark:bg-amber-950/10" : ""}`}
                data-testid={`coverage-alert-row-${r.ward_id}`} data-kind={r.alert ? "stranded" : "uncovered"}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium" data-testid={`coverage-alert-ward-${r.ward_id}`}>{r.ward_name || "Ward"}</span>
                    {r.zone_name && <span className="text-xs text-muted-foreground">{r.zone_name}</span>}
                    {r.alert ? (
                      <Badge variant="outline" className="text-[11px] gap-1 bg-amber-100 text-amber-800 border-amber-200 whitespace-nowrap" data-testid={`coverage-alert-badge-${r.ward_id}`}>
                        <ShieldAlert className="h-3 w-3" /> {n(r.stranded_no_mohalla)} stranded
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[11px] gap-1 bg-slate-100 text-slate-700 border-slate-200 whitespace-nowrap" data-testid={`coverage-alert-badge-${r.ward_id}`}>
                        <Users className="h-3 w-3" /> No collector
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5" data-testid={`coverage-alert-detail-${r.ward_id}`}>
                    {r.alert ? (
                      <>
                        {n(r.stranded_no_mohalla)} of {n(r.properties)} {plural(r.properties, "property", "properties")} {plural(r.stranded_no_mohalla, "has", "have")} no mohalla.
                        {r.mohalla_collectors?.length > 0 && <> By mohalla: {r.mohalla_collectors.slice(0, 3).map((c) => staffLabelText(c.name, c.employee_id)).join(", ")}{r.mohalla_collectors.length > 3 ? ` +${r.mohalla_collectors.length - 3} more` : ""}.</>}
                        {" "}Fill their mohallas or map a whole-ward collector.
                      </>
                    ) : (
                      <>{n(r.properties)} {plural(r.properties, "property", "properties")} with nobody mapped to this ward. Map a collector under Users &amp; Staff.</>
                    )}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    {canBackfill && r.alert && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setBackfillWard(r.ward_id)} data-testid={`coverage-alert-backfill-${r.ward_id}`}>
                        <MapPinned className="h-3.5 w-3.5 mr-1" /> Fill mohallas
                      </Button>
                    )}
                    <Button asChild size="sm" variant="ghost" className="h-7 text-xs" data-testid={`coverage-alert-territories-${r.ward_id}`}>
                      <Link to={territoriesHref(r)}><Users className="h-3.5 w-3.5 mr-1" /> Territories</Link>
                    </Button>
                  </div>
                )}
              </li>
            ))}
            {total > limit && (
              <li className="px-3 py-1.5 bg-muted/30">
                <button type="button" onClick={() => setShowAll((v) => !v)}
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  data-testid="coverage-alerts-show-more">
                  {showAll ? "Show fewer" : `Show ${n(total - limit)} more ${plural(total - limit, "ward", "wards")}`}
                </button>
              </li>
            )}
          </ul>
        ))}

        {canBackfill && (
          <MohallaBackfillDialog code={code} open={backfillWard !== null} onOpenChange={(v) => { if (!v) setBackfillWard(null); }}
            wardId={backfillWard || null} onDone={afterBackfill} />
        )}
        {canBackfill && <MohallaUploadDialog code={code} open={uploadOpen} onOpenChange={setUploadOpen} onDone={afterBackfill} />}
      </CardContent>
    </Card>
  );
}
